import React, {
    createContext,
    useCallback,
    useContext,
    useRef,
    useState,
} from 'react';
import { EditorState, ContentState } from 'draft-js';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import { get, post, patch, mutlipart_post } from '../api/api';
import { getUser } from "../api/auth";

interface EventFormContextType {
    // Gestion de l'état du formulaire
    step: number;
    setStep: React.Dispatch<React.SetStateAction<number>>;

    firstFormData: Record<string, any>;
    updateFirstFormData: (key: string, value: any) => void;
    secondFormData: Record<string, any>;
    updateSecondFormData: (key: string, value: any) => void;
    thirdFormData: any[];
    setThirdFormData: React.Dispatch<React.SetStateAction<any[]>>;
    fourthFormData: any[];
    setFourthFormData: React.Dispatch<React.SetStateAction<any[]>>;

    newArtiste: string;
    setNewArtiste: React.Dispatch<React.SetStateAction<string>>;
    artistes: string[];
    setArtistes: React.Dispatch<React.SetStateAction<string[]>>;

    searchCateg: any[];
    setSearchCateg: React.Dispatch<React.SetStateAction<any[]>>;
    localisations: any[];
    setLocalisations: React.Dispatch<React.SetStateAction<any[]>>;
    selectedLocalisation?: string;
    setSelectedLocalisation: React.Dispatch<React.SetStateAction<string | undefined>>;

    orga: { name: string; logo: string };
    setOrga: React.Dispatch<React.SetStateAction<{ name: string; logo: string }>>;
    orgaName: string;
    setOrgaName: React.Dispatch<React.SetStateAction<string>>;
    orgaLogo: string;
    setOrgaLogo: React.Dispatch<React.SetStateAction<string>>;

    latLng: [number, number];
    setLatLng: React.Dispatch<React.SetStateAction<[number, number]>>;
    zoom: number;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    address: string;
    setAddress: React.Dispatch<React.SetStateAction<string>>;
    city: string;
    setCity: React.Dispatch<React.SetStateAction<string>>;

    editorState: EditorState;
    setEditorState: React.Dispatch<React.SetStateAction<EditorState>>;

    fileInputRef: React.RefObject<HTMLInputElement>;

    // Fonctions API
    loadLocalisations: () => Promise<void>;
    loadSubCategories: () => Promise<void>;
    loadEvent: (eventId: string) => Promise<void>;
    submitEvent: () => Promise<void>;
    updateEvent: (eventId: string) => Promise<void>;
    uploadBanner: (file: File) => Promise<void>;
    uploadLogo: (file: File) => Promise<void>;

    // Fonctions de validation
    validateStep1: () => Promise<boolean>;
    validateStep2: () => Promise<boolean>;
    validateStep3: () => Promise<boolean>;
    validateStep4: () => Promise<boolean>;
    validateStep5: () => Promise<boolean>;
}

const EventFormContext = createContext<EventFormContextType | undefined>(undefined);

export const EventFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // --- États du formulaire ---
    const [step, setStep] = useState<number>(1);
    const [firstFormData, setFirstFormData] = useState<Record<string, any>>({});
    const [secondFormData, setSecondFormData] = useState<Record<string, any>>({});
    const [thirdFormData, setThirdFormData] = useState<any[]>([]);
    const [fourthFormData, setFourthFormData] = useState<any[]>([]);
    const [newArtiste, setNewArtiste] = useState<string>('');
    const [artistes, setArtistes] = useState<string[]>([]);
    const [searchCateg, setSearchCateg] = useState<any[]>([]);
    const [localisations, setLocalisations] = useState<any[]>([]);
    const [selectedLocalisation, setSelectedLocalisation] = useState<string | undefined>(undefined);
    const [orga, setOrga] = useState({ name: '', logo: '' });
    const [orgaName, setOrgaName] = useState<string>('');
    const [orgaLogo, setOrgaLogo] = useState<string>('');
    const [latLng, setLatLng] = useState<[number, number]>([46.59822, 2.49561]);
    const [zoom, setZoom] = useState<number>(5);
    const [address, setAddress] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
    const fileInputRef = useRef<HTMLInputElement>(null);
    const user = getUser();

    // Helpers pour mettre à jour les objets de formulaire
    const updateFirstFormData = (key: string, value: any) => {
        setFirstFormData((prev) => ({ ...prev, [key]: value }));
    };

    const updateSecondFormData = (key: string, value: any) => {
        setSecondFormData((prev) => ({ ...prev, [key]: value }));
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0); // On enlève l'heure pour ne comparer que la date
    // --- Schémas de validation avec Yup ---
    const step1Schema = yup.object().shape({
        name: yup.string().required("Le nom de l'événement est requis"),
        start_date: yup
            .date()
            .required("La date de début est requise")
            .typeError("La date de début doit être une date valide")
            .min(today, "La date de début ne peut pas être antérieure à aujourd'hui"),
        end_date: yup
            .date()
            .required("La date de fin est requise")
            .typeError("La date de fin doit être une date valide")
            .min(yup.ref("start_date"), "La date de fin doit être après la date de début"),
        price: yup
            .number()
            .typeError("Le prix doit être un nombre")
            .required("Le prix est requis"),
        icon: yup
            .object()
            .required("L'image est requis"),
        // ticketing_link: yup
        //     .string()
        //     .url("Le lien doit être une URL valide")
        //     .required("Le lien de billetterie est requis"),
    });

    const step2Schema = yup.object().shape({
        localisation: yup.string().required("La localisation est requise"),
        location_name: yup.string().required("Le nom du lieu est requis"),
        address: yup.string().required("L'adresse est requise"),
        city: yup.string().required("La ville est requise"),
    });

    const step3Schema = yup
        .array()
        .of(yup.object().required())
        .min(1, "Au moins une catégorie doit être sélectionnée");

    const step4Schema = yup
        .array()
        .of(yup.string().required("Chaque artiste doit être renseigné"))
        .min(1, "Au moins un artiste doit être ajouté");

    const step5Schema = yup.object().shape({
        orgaName: yup.string().required("Le nom de l'organisateur est requis"),
    });

    // --- Fonctions de validation par étape ---
    const validateStep1 = async (): Promise<boolean> => {
        try {
            await step1Schema.validate(firstFormData, { abortEarly: false });
            return true;
        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                Swal.fire({
                    title: "Erreurs dans les informations de l'événement",
                    html: `<ul>${err.errors.map((error) => `<li>${error}</li>`).join('')}</ul>`,
                    icon: "error",
                });
            }
            return false;
        }
    };

    const validateStep2 = async (): Promise<boolean> => {
        try {
            await step2Schema.validate(secondFormData, { abortEarly: false });
            return true;
        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                Swal.fire({
                    title: "Erreurs dans les informations de l'événement",
                    html: `<ul>${err.errors.map((error) => `<li>${error}</li>`).join('')}</ul>`,
                    icon: "error",
                });
            }
            return false;
        }
    };

    const validateStep3 = async (): Promise<boolean> => {
        try {
            await step3Schema.validate(thirdFormData, { abortEarly: false });
            return true;
        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                Swal.fire({
                    title: "Erreurs dans les informations de l'événement",
                    html: `<ul>${err.errors.map((error) => `<li>${error}</li>`).join('')}</ul>`,
                    icon: "error",
                });
            }
            return false;
        }
    };

    const validateStep4 = async (): Promise<boolean> => {
        try {
            await step4Schema.validate(fourthFormData, { abortEarly: false });
            return true;
        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                Swal.fire({
                    title: "Erreurs dans les informations de l'événement",
                    html: `<ul>${err.errors.map((error) => `<li>${error}</li>`).join('')}</ul>`,
                    icon: "error",
                });
            }
            return false;
        }
    };

    const validateStep5 = async (): Promise<boolean> => {
        try {
            await step5Schema.validate({ orgaName }, { abortEarly: false });
            return true;
        } catch (err: any) {
            if (err instanceof yup.ValidationError) {
                Swal.fire({
                    title: "Erreurs dans les informations de l'événement",
                    html: `<ul>${err.errors.map((error) => `<li>${error}</li>`).join('')}</ul>`,
                    icon: "error",
                });
            }
            return false;
        }
    };

    // --- Fonctions API intégrées au contexte ---
    const loadLocalisations = useCallback(async () => {
        try {
            const res = await get({ url: 'localisations' });
            setLocalisations(res.data);
        } catch (err) {
            console.error('Erreur lors du chargement des localisations :', err);
        }
    }, []);

    const loadSubCategories = useCallback(async () => {
        try {
            const res = await get({ url: 'sub-categories' });
            setSearchCateg(res.data);
        } catch (err) {
            console.error('Erreur lors du chargement des sous-catégories :', err);
        }
    }, []);

    const loadEvent = useCallback(async (eventId: string) => {
        try {
            const res = await get({ url: `events/${eventId}` });
            const data = res.data;

            // Exemple de parsing des dates
            const parseDateTime = (isoString: string) => {
                const dateObj = new Date(isoString);
                const pad = (num: number) => String(num).padStart(2, '0');
                return {
                    date: `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}`,
                    time: `${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`,
                };
            };

            let start_date = "";
            let start_hour = "00:00";
            let end_date = "";
            let end_hour = "00:00";

            if (data.start_date) {
                console.log(data.start_date)
                const parsedStart = parseDateTime(data.start_date);
                start_date = parsedStart.date;
                start_hour = parsedStart.time;
            }
            if (data.end_date) {
                const parsedEnd = parseDateTime(data.end_date);
                end_date = parsedEnd.date;
                end_hour = parsedEnd.time;
            }

            setFirstFormData({
                name: data.name,
                description: data.description,
                start_date: data.start_date,
                start_hour,
                end_date: data.end_date,
                end_hour,
                price: data.price,
                ticketing_link: data.ticketing_link,
                icon: data.urls_image?.[0]
                    ? { url: data.urls_image[0], name: 'Image sélectionnée' }
                    : null,
            });

            setSecondFormData({
                localisation: `/localisations/${data.localisation.id}`,
                location_name: data.location_name,
                address: data.address,
                postal_code: data.postal_code,
                city: data.city,
                location_coordinates_gps_x: data.location_coordinates_gps_x,
                location_coordinates_gps_y: data.location_coordinates_gps_y,
            });

            setThirdFormData(data.sub_categories);
            setArtistes(data.artists);
            setFourthFormData(data.artists);

            setOrga({
                name: data.organizer_name,
                logo: data.organizer_logo || '',
            });

            const contentState = ContentState.createFromText(data.description || '');
            setEditorState(EditorState.createWithContent(contentState));
        } catch (err) {
            console.error("Erreur lors du chargement de l'événement :", err);
        }
    }, []);

    const submitEvent = async () => {
        // Conversion des dates locales en UTC
        const startDateLocal = new Date(`${firstFormData.start_date.split("T")[0]}T${firstFormData.start_hour}`);
        const startDateUTC = startDateLocal.toISOString();
        const endDateLocal = new Date(`${firstFormData.end_date.split("T")[0]}T${firstFormData.end_hour}`);
        const endDateUTC = endDateLocal.toISOString();

        const formValues = {
            ...firstFormData,
            start_date: startDateUTC,
            end_date: endDateUTC,
            description: editorState.getCurrentContent().getPlainText('\u0001'),
            ...secondFormData,
            artists: fourthFormData,
            organizer_name: user?.name,
            urls_image: firstFormData.urls_image,
            sub_categories: thirdFormData.map((subCat) => `/sub-categories/${subCat.id}`),
            location_coordinates_gps_x: secondFormData.location_coordinates_gps_x.toString(),
            location_coordinates_gps_y: secondFormData.location_coordinates_gps_y.toString(),
            status: 'VALIDATED',
        };

        try {
            const res = await post({ url: 'events', data: formValues });
            Swal.fire({
                title: 'Succès',
                text: 'Votre événement a bien été créé.',
                icon: 'success',
            });
            window.location.href = `/events/show?id=${res.data.id}`;
        } catch (err) {
            Swal.fire({
                title: 'Erreur',
                text: "Une erreur est survenue lors de la création de l'événement.",
                icon: 'error',
            });
            console.error("Erreur lors de la soumission de l'événement :", err);
        }
    };

    const updateEvent = async (eventId: string) => {
        // Conversion des dates locales en UTC
        const startDateLocal = new Date(`${firstFormData.start_date.split("T")[0]}T${firstFormData.start_hour}`);
        const startDateUTC = startDateLocal.toISOString();
        const endDateLocal = new Date(`${firstFormData.end_date.split("T")[0]}T${firstFormData.end_hour}`);
        const endDateUTC = endDateLocal.toISOString();

        const formValues = {
            ...firstFormData,
            start_date: startDateUTC,
            end_date: endDateUTC,
            description: editorState.getCurrentContent().getPlainText('\u0001'),
            ...secondFormData,
            artists: fourthFormData,
            organizer_name: user?.name,
            urls_image: firstFormData.urls_image,
            sub_categories: thirdFormData.map(subCat => `/sub-categories/${subCat.id}`),
            location_coordinates_gps_x: secondFormData.location_coordinates_gps_x.toString(),
            location_coordinates_gps_y: secondFormData.location_coordinates_gps_y.toString(),
            status: 'VALIDATED',
        };

        try {
            await patch({ url: `events/${eventId}`, data: formValues });
            Swal.fire({
                title: 'Succès',
                text: "Votre événement a bien été modifié.",
                icon: 'success',
            });
            window.location.reload();
        } catch (err) {
            Swal.fire({
                title: 'Erreur',
                text: "Une erreur est survenue lors de la modification de l'événement.",
                icon: 'error',
            });
            console.error("Erreur lors de la mise à jour de l'événement :", err);
        }
    };

    const uploadBanner = async (file: File) => {
        try {
            const res = await mutlipart_post({ url: 'file_uploads', data: { file } });
            updateFirstFormData('icon', { url: res.data.url, name: file.name });
            updateFirstFormData('urls_image', [res.data.url]);
        } catch (err) {
            console.error("Erreur lors de l'upload de la bannière :", err);
        }
    };

    const uploadLogo = async (file: File) => {
        try {
            const res = await mutlipart_post({ url: 'file_uploads', data: { file } });
            setOrgaLogo(res.data.url);
        } catch (err) {
            console.error("Erreur lors de l'upload du logo :", err);
        }
    };

    return (
        <EventFormContext.Provider
            value={{
                step,
                setStep,
                firstFormData,
                updateFirstFormData,
                secondFormData,
                updateSecondFormData,
                thirdFormData,
                setThirdFormData,
                fourthFormData,
                setFourthFormData,
                newArtiste,
                setNewArtiste,
                artistes,
                setArtistes,
                searchCateg,
                setSearchCateg,
                localisations,
                setLocalisations,
                selectedLocalisation,
                setSelectedLocalisation,
                orga,
                setOrga,
                orgaName,
                setOrgaName,
                orgaLogo,
                setOrgaLogo,
                latLng,
                setLatLng,
                zoom,
                setZoom,
                address,
                setAddress,
                city,
                setCity,
                editorState,
                setEditorState,
                fileInputRef,
                loadLocalisations,
                loadSubCategories,
                loadEvent,
                submitEvent,
                updateEvent,
                uploadBanner,
                uploadLogo,
                validateStep1,
                validateStep2,
                validateStep3,
                validateStep4,
                validateStep5,
            }}
        >
            {children}
        </EventFormContext.Provider>
    );
};

export const useEventForm = () => {
    const context = useContext(EventFormContext);
    if (!context) {
        throw new Error('useEventForm must be used within an EventFormProvider');
    }
    return context;
};